'use client'

import { useState, useEffect } from 'react'
import Image from 'next/image'

interface SecYoutubeVideoProps {
  heading?: {
    title?: string
  }
  video?: {
    embed_url?: string
  }
}

export const fields = [
  'heading.title',
  'video.embed_url',
] as const

const getYouTubeVideoId = (url: string): string | null => {
  if (!url) return null
  const regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
  const match = url.match(regExp)
  return match && match[2].length === 11 ? match[2] : null
}

const buildEmbedUrl = (url: string, origin: string): string => {
  const videoId = getYouTubeVideoId(url)
  if (!videoId) return url
  const base = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`
  return origin ? `${base}&origin=${encodeURIComponent(origin)}` : base
}

export default function SecYoutubeVideo({ data }: { data: SecYoutubeVideoProps }) {
  const [playing, setPlaying] = useState(false)
  const [origin, setOrigin] = useState('')

  useEffect(() => {
    setOrigin(window.location.origin)
  }, [])

  if (!data) return null

  const embedUrl = data.video?.embed_url || ''
  const videoId = getYouTubeVideoId(embedUrl)
  const thumbnailUrl = videoId
    ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
    : null

  return (
    <section className='SecYoutubeVideo w-full py-16 lg:py-24 bg-sand-light'>
      <div className='container'>
        {data.heading?.title && (
          <h2 className='text-center mb-10 lg:mb-14'>
            {data.heading.title}
          </h2>
        )}

        {embedUrl && (
          <div className='relative rounded-2xl overflow-hidden shadow-xl group'>
            <div className='aspect-video relative bg-navy'>
              {playing ? (
                <iframe
                  src={buildEmbedUrl(embedUrl, origin)}
                  className='absolute inset-0 w-full h-full'
                  frameBorder='0'
                  allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                  allowFullScreen
                  title={data.heading?.title || 'YouTube video'}
                />
              ) : (
                <>
                  {thumbnailUrl && (
                    <Image
                      src={thumbnailUrl}
                      alt={data.heading?.title || 'Video thumbnail'}
                      fill
                      className='object-cover transition-transform duration-700 group-hover:scale-[1.02]'
                      unoptimized
                      priority
                    />
                  )}

                  <div className='absolute inset-0 bg-navy/25 group-hover:bg-navy/35 transition-colors duration-300' />

                  <button
                    onClick={() => setPlaying(true)}
                    aria-label='Play video'
                    className='absolute inset-0 flex items-center justify-center w-full cursor-pointer'
                  >
                    <span className='relative flex items-center justify-center w-11 h-11 lg:w-24 lg:h-24 rounded-full bg-white/90 shadow-2xl backdrop-blur-sm transition-all duration-300 group-hover:scale-110 group-hover:bg-white'>
                      <span className='absolute inset-0 rounded-full border-2 border-white/50 scale-[1.3] group-hover:scale-[1.55] transition-transform duration-500' />
                      <svg
                        viewBox='0 0 28 32'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                        className='w-3 h-3.5 lg:w-7 lg:h-8 ml-0.5 lg:ml-1'
                      >
                        <path
                          d='M2 2L26 16L2 30V2Z'
                          fill='#9E6735'
                          stroke='#9E6735'
                          strokeWidth='2'
                          strokeLinejoin='round'
                        />
                      </svg>
                    </span>
                  </button>

                  <div className='absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-navy/50 to-transparent pointer-events-none' />
                </>
              )}
            </div>
          </div>
        )}

        {embedUrl && (
          <p className='mt-6 text-sm text-gray-slate'>
            *Disclaimer: The recovery and post-operative information in this video is intended for Dr. Harmon’s patients only. Dr. Harmon may give instructions that differ from those in this video if he determines it is appropriate for a specific patient. Patients should always follow the instructions provided to them by Dr. Harmon and his team.
          </p>
        )}
      </div>
    </section>
  )
}
